<i18n>
{
  "fr": {
    "set-specific-indicator-btn": "Rendre spécifique",
    "set-specific-indicator-message": "En confirmant, vous allez créer un indicateur détaché du gabarit de projet",
    "specific-indicator-confirm": "Confirmer",
    "specific-indicator-cancel": "Annuler",
    "remove-specific-indicator-btn": "Supprimer les spécificités",
    "remove-specific-indicator-message": "En confirmant, vous allez supprimer toutes les spécificités et vous allez rattacher l’indicateur au gabarit de projet"
  },
  "en": {
    "set-specific-indicator-btn": "Set to specific",
    "set-specific-indicator-message": "By confirming, you will unlink this indicator from the project template",
    "specific-indicator-confirm": "Confirm",
    "specific-indicator-cancel": "Cancel",
    "remove-specific-indicator-btn": "Remove specifics",
    "remove-specific-indicator-message": "By confirming, you will delete all specific content and link this indicator to the project template"
  }
}
</i18n>

<template>
  <b-switch
    v-if="indicator"
    class="specific-switch"
    :rounded="isRounded"
    :outlined="isOutlined"
    :size="size"
    :aria-labelledby="labelSpecificSwitch"
    v-model="isSpecific"
    @input="toggleSwitch"
    >{{ labelSpecificSwitch }}</b-switch
  >
</template>

<script>
import i18n from '@/helpers/i18n'

export default {
  props: {
    indicator: { type: Object, required: true },
    projectId: String,
    size: {
      type: String,
      default: 'is-large'
    }
  },
  watch: {
    'indicator.id'() {
      // refresh isSpecific
      this.isSpecific = this.indicator.isSpecific
    }
  },
  data() {
    return {
      isRounded: false,
      isOutlined: false,
      isFullPage: true,
      isSpecific: this.indicator.isSpecific
    }
  },
  computed: {
    labelSpecificSwitch() {
      return this.indicator.isSpecific ? this.$t('remove-specific-indicator-btn') : this.$t('set-specific-indicator-btn')
    }
  },
  methods: {
    toggleSwitch(e) {
      const isCurrentlySpecific = !e
      this.$buefy.dialog.confirm({
        message: isCurrentlySpecific ? this.$t('remove-specific-indicator-message') : this.$t('set-specific-indicator-message'),
        cancelText: this.$t('specific-indicator-cancel'),
        confirmText: this.$t('specific-indicator-confirm'),
        type: 'is-primary',
        onConfirm: () => (isCurrentlySpecific ? this.removeSpecificIndicator() : this.addSpecificIndicator()),
        onCancel: () => (this.isSpecific = this.indicator.isSpecific)
      })
    },
    async addSpecificIndicator() {
      const loadingComponent = this.$buefy.loading.open({
        container: this.isFullPage ? null : this.$refs.element.$el
      })
      try {
        await this.$apollo.mutate({
          mutation: this.$options.mutation.CreateSpecificProjectIndicator,
          variables: {
            projectId: this.projectId,
            indicatorId: this.indicator.id,
            language: i18n.contentLanguage()
          },
          update: (cache, { data }) => {
            this.$emit('on-set-specific', data.createSpecificProjectIndicator.indicator.id)
            loadingComponent.close()
          }
        })
        this.$success()
      } finally {
        loadingComponent.close()
      }
    },
    async removeSpecificIndicator() {
      const loadingComponent = this.$buefy.loading.open({
        container: this.isFullPage ? null : this.$refs.element.$el
      })
      try {
        await this.$apollo.mutate({
          mutation: this.$options.mutation.RemoveSpecificProjectIndicator,
          variables: {
            projectId: this.projectId,
            indicatorId: this.indicator.id,
            language: i18n.contentLanguage()
          },
          update: (cache, { data }) => {
            this.$emit('on-remove-specific', data.removeSpecificProjectIndicator.templateIndicator.id)
            loadingComponent.close()
          }
        })
        this.$success()
      } finally {
        loadingComponent.close()
      }
    }
  }
}
</script>

<graphql>
mutation CreateSpecificProjectIndicator($projectId: ID!, $indicatorId: ID!, $language: ContentLanguage!) {
  createSpecificProjectIndicator(input: { projectId: $projectId, indicatorId: $indicatorId, language: $language }) {
    indicator {
      id
      language
      isSpecific
      status
    }
  }
}

mutation RemoveSpecificProjectIndicator($projectId: ID!, $indicatorId: ID!, $language: ContentLanguage!) {
  removeSpecificProjectIndicator(input: { projectId: $projectId, indicatorId: $indicatorId, language: $language }) {
    templateIndicator {
      id
      language
      isSpecific
      status
    }
  }
}
</graphql>

<style lang="scss">
@import '@/scss/specific-switch';
</style>
